import * as I from 'immutable';

import {FeatureId, NumberedFeatureCollection} from './ManageFeaturesProvider';

export interface UploadEdits {
  // Display-name overrides, keyed by feature index (id - 1, since ids were
  // +1'd at FC creation to avoid id 0). One entry per feature with a name.
  nameEdits: Record<string, string>;
  // Per-location part names, same keying. Only populated for multi-location
  // features (a shared name, or a name matching an existing property) in
  // 'group' (combine) mode with a non-empty part name. Lens labels any
  // feature with a part name "Name - Part", so a single-location property
  // must not get one. 'individual' (keep-separate) mode emits nothing here so
  // each location lands as its own property under its (now per-row edited)
  // display name.
  partNameEdits: Record<string, string>;
  // Indices of features to drop from the upload, driven entirely by selection
  // (removed × rows reach this set via the selection-state chain upstream).
  featureIdsToExclude: number[];
}

/**
 * Pure assembly of the args sent to `uploadFeaturesFromFile`. Lifted out of
 * the Provider so combine/separate, removed-location, and part-name behavior
 * can be exercised without mounting React or hitting the API — see
 * `uploadEdits.spec.ts` for the scenario table.
 *
 * The group identity is the edited display name, the same key the table
 * groups rows by (`buildPropertyGroups`).
 */
export function buildUploadEdits(input: {
  uploadedFeatureCollection: NumberedFeatureCollection | null;
  nameValues: I.Map<FeatureId, string> | null;
  partNameValues: I.Map<FeatureId, string | null> | null;
  selectedFeatureIds: I.Set<FeatureId>;
  groupModeByName: I.Map<string, 'group' | 'individual'>;
  warningsMap: Record<string, string[] | null>;
}): UploadEdits {
  const {
    uploadedFeatureCollection,
    nameValues,
    partNameValues,
    selectedFeatureIds,
    groupModeByName,
    warningsMap,
  } = input;

  const features = uploadedFeatureCollection?.features ?? [];

  const featureIdsToExclude = features
    .filter((f) => !selectedFeatureIds.has(f.id))
    .map((f) => f.id - 1);

  // Counts only uploaded features, so a group left with one checked location
  // lands as a single-location property.
  const nameCount = new Map<string, number>();
  nameValues?.forEach((name, featureId) => {
    if (!selectedFeatureIds.has(featureId!)) return;
    nameCount.set(name ?? '', (nameCount.get(name ?? '') ?? 0) + 1);
  });

  const nameEdits: Record<string, string> = {};
  const partNameEdits: Record<string, string> = {};
  nameValues?.forEach((name, featureId) => {
    const index = String(featureId! - 1);
    const partName = (partNameValues?.get(featureId!) ?? '') || '';
    const groupMode = groupModeByName.get(name ?? '') ?? 'group';
    const isMultiLocation =
      (nameCount.get(name ?? '') ?? 0) > 1 || !!warningsMap[String(featureId)]?.length;
    nameEdits[index] = name ?? '';
    if (groupMode === 'group' && isMultiLocation && partName) {
      partNameEdits[index] = partName;
    }
  });

  return {nameEdits, partNameEdits, featureIdsToExclude};
}
